// ================== State ==================
let currentMode = null;
let scrollPosition = 0;
let lastScrollY = window.scrollY;
let ticking = false;

// ================== Interaction State ==================
let hasMoved = false;

// Wait until the user moves the mouse once after page load
window.addEventListener("mousemove", () => (hasMoved = true), { once: true });

// Clean up overlay when navigating away
window.addEventListener("beforeunload", () => {
  document
    .querySelector(".custom-header__desktop-overlay")
    ?.classList.remove("active");
});

// ================== Helpers ==================
function disableBodyScroll() {
  scrollPosition = window.scrollY;
  document.body.style.position = "fixed";
  document.body.style.top = `-${scrollPosition}px`;
  document.body.style.left = "0";
  document.body.style.right = "0";
  document.body.style.width = "100%";
}

function enableBodyScroll() {
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  document.body.style.width = "";
  window.scrollTo(0, scrollPosition);
}

function debounce(fn, delay) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), delay);
  };
}

// ================== Desktop ==================
function toggleDesktopOverlay(show) {
  const overlay = document.querySelector(".custom-header__desktop-overlay");
  if (!overlay) return;

  if (show) {
    overlay.classList.add("active");
    disableBodyScroll();
  } else {
    overlay.classList.remove("active");
    enableBodyScroll();
  }
}

function initDesktopMenu() {
  // Reset mobile overlay if it exists
  const overlay = document.querySelector(".custom-header__mobile-overlay");
  overlay?.classList.remove("active");
  overlay
    ?.querySelectorAll(".custom-header__mobile-view")
    .forEach((v) => v.classList.remove("active"));

  const desktopOverlay = document.querySelector(
    ".custom-header__desktop-overlay"
  );
  let overlayTimeout;

  function showOverlay() {
    clearTimeout(overlayTimeout);
    toggleDesktopOverlay(true);
  }

  function hideOverlay() {
    overlayTimeout = setTimeout(() => {
      toggleDesktopOverlay(false);
    }, 100); // small delay to prevent flicker
  }

  document.querySelectorAll(".custom-header__item").forEach((item) => {
    const submenu = item.querySelector(".custom-header__submenu");
    if (!submenu) return;

    // Overlay only triggers for items with a submenu
    item.addEventListener("mouseenter", () => {
      if (!hasMoved) return; // prevent overlay before first mouse move
      showOverlay();
    });
    item.addEventListener("mouseleave", hideOverlay);

    const trigger = item.querySelector(".custom-header__link");
    const triggerArrow = item.querySelector(".header-arrow");
    const childLinks = submenu.querySelectorAll(
      ".custom-header__submenu-childlinks a"
    );
    const grandchildContainer = submenu.querySelector(
      ".custom-header__submenu-grandchildren"
    );
    const grandchildLists = submenu.querySelectorAll(".grandchild-list");

    let timeoutId;
    function showSubmenu() {
      clearTimeout(timeoutId);
      submenu.style.visibility = "visible";
      submenu.style.opacity = "1";
      submenu.style.pointerEvents = "auto";
      trigger.setAttribute("aria-expanded", "true");
      triggerArrow?.classList.add("rotated");
    }

    function hideSubmenu() {
      timeoutId = setTimeout(() => {
        submenu.style.visibility = "hidden";
        submenu.style.opacity = "0";
        submenu.style.pointerEvents = "none";
        trigger.setAttribute("aria-expanded", "false");
        triggerArrow?.classList.remove("rotated");

        grandchildLists.forEach((div) => (div.style.display = "none"));
        grandchildContainer?.classList.remove("is-visible");
      }, 200);
    }

    function showGrandchildList(index) {
      let anyVisible = false;
      let hasChildren = false;

      grandchildLists.forEach((div) => {
        const isMatch = div.dataset.childIndex === index;
        if (div.children.length) hasChildren = true;
        div.style.display = isMatch ? "flex" : "none";
        if (isMatch) anyVisible = true;
      });

      grandchildContainer?.classList.toggle(
        "is-visible",
        anyVisible && hasChildren
      );
    }

    item.addEventListener("mouseenter", showSubmenu);
    item.addEventListener("mouseleave", hideSubmenu);
    submenu.addEventListener("mouseenter", showSubmenu);
    submenu.addEventListener("mouseleave", hideSubmenu);

    childLinks.forEach((link) => {
      link.addEventListener("mouseenter", () => {
        const index = link.dataset.childIndex;
        showGrandchildList(index);
      });
    });
  });
}

// ================== Mobile ==================
function initMobileMenu() {
  // Hide all desktop submenus
  document.querySelectorAll(".custom-header__submenu").forEach((submenu) => {
    submenu.style.visibility = "hidden";
    submenu.style.opacity = "0";
    submenu.style.pointerEvents = "none";
  });

  const overlay = document.querySelector(".custom-header__mobile-overlay");
  const mainView = overlay?.querySelector(".custom-header__mobile-view--main");
  const subViews = overlay?.querySelectorAll(
    ".custom-header__mobile-view--submenu"
  );

  overlay?.classList.remove("active");
  mainView?.classList.add("active");
  subViews?.forEach((v) => v.classList.remove("active"));

  // Clean previous event listeners by cloning nodes
  document.querySelectorAll(".custom-header__menu-toggle").forEach((btn) => {
    const newBtn = btn.cloneNode(true);
    btn.parentNode.replaceChild(newBtn, btn);

    newBtn.addEventListener("click", () => {
      overlay.classList.toggle("active");
      if (overlay.classList.contains("active")) {
        mainView?.classList.add("active");
        disableBodyScroll();
      } else {
        mainView?.classList.remove("active");
        subViews?.forEach((v) => v.classList.remove("active"));
        enableBodyScroll();
      }
    });
  });

  overlay?.querySelectorAll(".has-children").forEach((btn) => {
    const index = btn.dataset.index;
    const newBtn = btn.cloneNode(true);
    btn.parentNode.replaceChild(newBtn, btn);

    newBtn.addEventListener("click", () => {
      mainView.classList.remove("active");
      overlay
        ?.querySelector(`[data-parent-index="${index}"]`)
        ?.classList.add("active");
    });
  });

  overlay?.querySelectorAll(".custom-header__back").forEach((btn) => {
    const newBtn = btn.cloneNode(true);
    btn.parentNode.replaceChild(newBtn, btn);

    newBtn.addEventListener("click", () => {
      subViews.forEach((v) => v.classList.remove("active"));
      mainView.classList.add("active");
    });
  });
}

// ================== Header + Overlay Positioning ==================
function updateOverlayTop() {
  setTimeout(() => {
    const banner = document.getElementById("custom-utility-nav-banner");
    const desktopOverlay = document.querySelector(
      ".custom-header__desktop-overlay"
    );
    const mobileOverlay = document.querySelector(
      ".custom-header__mobile-overlay"
    );
    const header = document.querySelector(".custom-header");

    const bannerHeight =
      banner && banner.offsetHeight && banner.style.display !== "none"
        ? banner.offsetHeight
        : 0;

    const headerHeight = header ? header.offsetHeight : 0;

    // Desktop overlay
    if (desktopOverlay) {
      desktopOverlay.style.top = bannerHeight + headerHeight + "px";
    }

    // Mobile overlay
    if (mobileOverlay) {
      mobileOverlay.style.top = bannerHeight + "px";
    }
  }, 50); // small delay to ensure correct measurements
}

function updateHeaderTop() {
  const bannerWrapper = document.getElementById("custom-utility-nav-wrapper");
  const banner = document.getElementById("custom-utility-nav-banner");
  const header = document.querySelector(".custom-header__desktop");

  if (!header || !bannerWrapper || !banner) return;

  const bannerHeight =
    banner && banner.offsetHeight && banner.style.display !== "none"
      ? banner.offsetHeight
      : 0;

  // Set the wrapper's height to match the banner's height.
  // This prevents content below from jumping when the fixed banner is shown or hidden.
  bannerWrapper.style.height = bannerHeight + "px";
  header.style.top = bannerHeight + "px";
}

// ================== Mode Switching ==================
function handleMenu() {
  if (window.matchMedia("(min-width: 1025px)").matches) {
    if (currentMode !== "desktop") {
      initDesktopMenu();
      currentMode = "desktop";
    }
  } else {
    if (currentMode !== "mobile") {
      initMobileMenu();
      currentMode = "mobile";
    }
  }
}

// ================== Scroll Behavior ==================
function handleHeaderScroll() {
  const header = document.querySelector(".custom-header__desktop");
  if (!header) return;

  const currentScrollY = window.scrollY;

  if (currentScrollY > lastScrollY && currentScrollY > 100) {
    // scrolling down
    header.classList.add("is-hidden");
  } else {
    // scrolling up
    header.classList.remove("is-hidden");
  }

  lastScrollY = currentScrollY;
  ticking = false;
}

// ================== Init ==================
document.addEventListener("DOMContentLoaded", () => {
  document.body.classList.add("js-enabled");
  handleMenu();
  updateHeaderTop();
  updateOverlayTop();

  // Recalculate overlay if banner closes
  const bannerCloseBtn = document.getElementById(
    "custom-utility-nav-banner__banner-close"
  );
  bannerCloseBtn?.addEventListener("click", updateOverlayTop);
});

window.addEventListener(
  "resize",
  debounce(() => {
    handleMenu();
    updateHeaderTop();
    updateOverlayTop();
  }, 150)
);

window.addEventListener("scroll", () => {
  if (!ticking) {
    requestAnimationFrame(handleHeaderScroll);
    ticking = true;
  }
});
